import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, Trash2, Plus, Zap, Pencil, Search } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { downloadDocument } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ExpiryCountdown from "../components/ExpiryCountdown";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const KIND_LABEL = {
  richtext: "Rich Text",
  convert: "Converted",
  "reverse:docx": "PDF → Word",
  "reverse:images": "PDF → Images",
  "template:invoice": "Invoice",
  "template:resume": "Resume",
  "template:certificate": "Certificate",
  "template:cover_letter": "Cover Letter",
  "template:report": "Report",
};

const isEditable = (kind) => kind === "richtext" || kind.startsWith("template:");

const FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "richtext", label: "Documents", match: (k) => k === "richtext" },
  { key: "template", label: "Templates", match: (k) => k.startsWith("template:") },
  { key: "convert", label: "Conversions", match: (k) => k === "convert" },
  { key: "reverse", label: "Extracts", match: (k) => k.startsWith("reverse:") },
];

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get("/documents");
      setDocs(res.data);
    } catch (e) {
      setDocs([]);
    }
  };
  useEffect(() => { load(); }, []);

  const dl = async (d) => {
    setBusy(d.id);
    try { await downloadDocument(d.id, d.title, d.ext || "pdf"); }
    catch (e) { toast.error("Download failed"); }
    finally { setBusy(null); }
  };

  const reedit = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}`);
      const source = res.data.source;
      if (!source) { toast.error("This document can't be re-edited"); return; }
      navigate("/studio", { state: { reedit: source } });
    } catch (e) { toast.error("Could not open for editing"); }
  };

  const del = async (d) => {
    try {
      await api.delete(`/documents/${d.id}`);
      setDocs((cur) => cur.filter((x) => x.id !== d.id));
      toast.success("Document deleted");
    } catch (e) { toast.error("Could not delete"); }
  };

  const fmtDate = (s) => new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  const matchFn = FILTERS.find((f) => f.key === filter)?.match || (() => true);
  const filtered = useMemo(() => {
    if (!docs) return [];
    const q = query.trim().toLowerCase();
    return docs.filter((d) => matchFn(d.kind) && (!q || (d.title || "").toLowerCase().includes(q)));
  }, [docs, query, filter]);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">My Documents</h1>
            <p className="text-slate-600 mt-1">All the PDFs you've created.</p>
          </div>
          <Link to="/studio">
            <Button data-testid="documents-new-button" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
              <Plus className="h-4 w-4 mr-1.5" /> New PDF
            </Button>
          </Link>
        </div>

        {docs === null ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
          </div>
        ) : docs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
            <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-7 w-7 text-slate-400" />
            </div>
            <h3 className="font-display text-xl font-semibold">No documents yet</h3>
            <p className="text-slate-500 mt-1">Create your first PDF in the studio.</p>
            <Link to="/studio"><Button className="mt-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">Go to Studio</Button></Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1 max-w-sm">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="documents-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by title…"
                  className="bg-white pl-9"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`documents-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      filter === f.key ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="documents-no-results">
                No documents match your search.
              </div>
            ) : (
              <div className="space-y-3" data-testid="my-documents-list">
                {filtered.map((d) => (
              <div key={d.id} data-testid={`document-row-${d.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 hover:border-indigo-300 transition-colors">
                <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{d.title}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{KIND_LABEL[d.kind] || "PDF"}</span>
                    {fmtDate(d.created_at)} · {(d.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <ExpiryCountdown createdAt={d.created_at} className="hidden md:inline-flex" />
                <span className="hidden sm:flex items-center gap-1 text-xs font-mono text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                  <Zap className="h-3 w-3 fill-amber-500 text-amber-500" /> {d.credits_spent}
                </span>
                {d.drive_link && (
                  <a data-testid={`drive-link-${d.id}`} href={d.drive_link} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full hover:bg-emerald-100">
                    Drive
                  </a>
                )}
                {isEditable(d.kind) && (
                  <Button
                    data-testid={`reedit-doc-${d.id}`}
                    onClick={() => reedit(d)}
                    variant="outline"
                    className="rounded-full border-slate-300"
                  >
                    <Pencil className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Re-edit</span>
                  </Button>
                )}
                <Button
                  data-testid={`download-pdf-button-${d.id}`}
                  onClick={() => dl(d)}
                  disabled={busy === d.id}
                  variant="outline"
                  className="rounded-full border-slate-300"
                >
                  <Download className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Download</span>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-doc-${d.id}`} className="text-slate-400 hover:text-red-500 p-2">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this file from our servers?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Make sure your download has finished. This permanently wipes “{d.title}” from our servers and can’t be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-delete-doc">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-delete-doc-${d.id}`} onClick={() => del(d)} className="bg-red-600 hover:bg-red-700">
                        Delete permanently
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
